import { useSectionAccess } from "../../auth/useSectionAccess";
import { useMemo, useState } from "react";
import { formatDateTime } from "../../lib/format";
import { useTranslation } from "react-i18next";
import { tmApi, type TmCommentView } from "../../api/tm";
import { useToast } from "../Toast";
import type { ApiError } from "../../api/client";
import { MentionTextarea } from "./MentionTextarea";

type Props = {
  taskId: string;
  comments: TmCommentView[];
  onAdded: () => Promise<void>;
};

/**
 * Threaded comments — top-level comments render a flat list and any replies
 * appear indented under their parent. Reply tree is one level deep visually
 * even if the server stores deeper threads; replies-of-replies attach to the
 * nearest visible parent so the UI doesn't accumulate indentation rabbit
 * holes.
 */
export function TmCommentThread({ taskId, comments, onAdded }: Props) {
  const { canWrite } = useSectionAccess("tm_tasks");
  const { t } = useTranslation();
  const toast = useToast();
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [replyParentId, setReplyParentId] = useState<string | null>(null);
  const [replyDraft, setReplyDraft] = useState("");

  // Group comments: top-level entries keep their own id, replies bucket under parent.
  const { roots, repliesByParent } = useMemo(() => {
    const r: TmCommentView[] = [];
    const m = new Map<string, TmCommentView[]>();
    for (const c of comments) {
      if (c.parentCommentId == null) {
        r.push(c);
      } else {
        const arr = m.get(c.parentCommentId) ?? [];
        arr.push(c);
        m.set(c.parentCommentId, arr);
      }
    }
    return { roots: r, repliesByParent: m };
  }, [comments]);

  async function send(parentId: string | null, content: string) {
    if (!canWrite) return;
    const trimmed = content.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      await tmApi.addComment(taskId, trimmed, parentId);
      if (parentId == null) {
        setDraft("");
      } else {
        setReplyDraft("");
        setReplyParentId(null);
      }
      await onAdded();
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {roots.length === 0 ? (
        <div style={{ color: "var(--ink-3)", fontSize: 13 }}>
          {t("tm.comments.empty", "No comments yet.")}
        </div>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
          {roots.map((c) => (
            <li key={c.id}>
              <CommentCard
                c={c}
                replyOpen={replyParentId === c.id}
                onToggleReply={canWrite ? () => {
                  setReplyParentId((prev) => (prev === c.id ? null : c.id));
                  setReplyDraft("");
                } : undefined}
              />
              {(repliesByParent.get(c.id) ?? []).map((reply) => (
                <div key={reply.id} style={{ marginLeft: 28, marginTop: 6 }}>
                  <CommentCard c={reply} dim />
                </div>
              ))}
              {replyParentId === c.id && (
                <div style={{ marginLeft: 28, marginTop: 6 }}>
                  <ReplyForm
                    value={replyDraft}
                    onChange={setReplyDraft}
                    onCancel={() => { setReplyParentId(null); setReplyDraft(""); }}
                    onSubmit={() => void send(c.id, replyDraft)}
                    busy={busy || !canWrite}
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <MentionTextarea
          value={draft}
          onChange={setDraft}
          rows={3}
          maxLength={5000}
          placeholder={t("tm.comments.placeholder", "Add a comment…  Type @ to mention someone")}
          disabled={!canWrite || busy}
        />
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            type="button"
            className="btn primary sm"
            onClick={() => void send(null, draft)}
            disabled={!canWrite || busy || !draft.trim()}
          >
            {busy ? t("tm.comments.sending", "Sending…") : t("tm.comments.send", "Send")}
          </button>
        </div>
      </div>
    </div>
  );
}

function CommentCard({
  c, dim, replyOpen, onToggleReply,
}: {
  c: TmCommentView;
  dim?: boolean;
  replyOpen?: boolean;
  onToggleReply?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div style={{
      padding: 12,
      border: "1px solid var(--border)",
      borderRadius: 8,
      background: dim ? "var(--surface-2)" : "var(--surface)",
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        marginBottom: 6,
        fontSize: 12, color: "var(--ink-3)",
      }}>
        <span style={{
          display: "grid", placeItems: "center",
          width: 24, height: 24, borderRadius: 999,
          background: "var(--brand-primary)",
          color: "var(--on-primary)",
          fontSize: 10, fontWeight: 700,
        }}>
          {(c.authorName || c.authorEmail).slice(0, 2).toUpperCase()}
        </span>
        <b style={{ color: "var(--ink)" }}>{c.authorName}</b>
        <span>· {formatDateTime(c.createdAt)}</span>
        {onToggleReply && (
          <button
            type="button"
            onClick={onToggleReply}
            style={{
              marginLeft: "auto",
              background: "transparent", border: "none",
              color: "var(--ink-3)", cursor: "pointer",
              fontSize: 12,
            }}
          >
            {replyOpen ? t("action.cancel", "Cancel") : t("tm.comments.reply", "Reply")}
          </button>
        )}
      </div>
      <div style={{ color: "var(--ink-2)", fontSize: 14, whiteSpace: "pre-wrap" }}>
        {c.content}
      </div>
    </div>
  );
}

function ReplyForm({
  value, onChange, onSubmit, onCancel, busy,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <MentionTextarea
        value={value}
        onChange={onChange}
        rows={2}
        maxLength={5000}
        placeholder={t("tm.comments.replyPlaceholder", "Reply…  Type @ to mention someone")}
        autoFocus
        disabled={busy}
      />
      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
        <button type="button" className="btn sm" onClick={onCancel} disabled={busy}>{t("action.cancel", "Cancel")}</button>
        <button type="button" className="btn primary sm" onClick={onSubmit} disabled={busy || !value.trim()}>
          {busy ? t("tm.comments.sending", "Sending…") : t("tm.comments.reply", "Reply")}
        </button>
      </div>
    </div>
  );
}
